﻿namespace Illy.SafeStack.Client

open System

type Time = Time of hours:int * mins:int * secs:int

module Time =
    let scalar (Time (h,m,s)) =
        (h*3600)+(m*60)+s

    let apply (d: DateTime) t =
        d.AddSeconds(scalar t |> float)

    let min = Time(0,0,0)
    let max = Time(23,59,59)

    let toMinuteString (Time (h,m,_)) =
        let hour = sprintf "%02i" h
        let min = sprintf "%02i" m
        hour + ":" + min

    let fromDateTime (dt: DateTime) =
        Time(dt.Hour, dt.Minute, dt.Second)

    let toDateTime t = apply (new DateTime(1900, 1, 1)) t

[<RequireQualifiedAccess>]
type ParsedTime =
    | Empty
    | Invalid of string
    | Valid of Time

module ParsedTime =
    open System
    open System.Text.RegularExpressions

    let regex = Regex("""^(\d|0\d|1\d|2[0-3])([\.:][0-5]\d)?$""")
    
    let formatError =
        function
        | ParsedTime.Invalid str -> sprintf "\"%s\" is not a valid 24-hour time." str
        | _ -> ""

    let toOption =
        function
        | ParsedTime.Valid v -> Some v
        | _ -> None

    let fromOption =
        function
        | None -> ParsedTime.Empty
        | Some x -> ParsedTime.Valid x

    let toDateTimeOption =
        toOption >> Option.map (Time.apply (new DateTime(1900, 1, 1)))

    let fromDateTimeOption (date: DateTime option) =
        date |> Option.map (fun d -> Time(d.Hour, d.Minute, d.Second)) |> fromOption
    
    let private parse input =
        let res = regex.Match(input)

        if not res.Success then
            ParsedTime.Invalid input
        else
            let hours = res.Groups.[1].Value |> int
            
            let mins = 
                if res.Groups.Count > 2 && res.Groups.[2] <> null then
                    res.Groups.[2].Value.Substring(1) |> int
                else
                    0

            Time(hours, mins, 0) |> ParsedTime.Valid

    let ofString str =
        if String.IsNullOrWhiteSpace str then 
            (ParsedTime.Empty, None)
        else
            match parse str with
            | ParsedTime.Empty as x -> x, None
            | ParsedTime.Valid _ as x -> x, None
            | ParsedTime.Invalid _ as x -> x, formatError x |> Some 

    let toString formatter = 
        function
        | ParsedTime.Empty -> ""
        | ParsedTime.Valid d -> formatter d
        | ParsedTime.Invalid str -> str

    let formatToOption formatter = 
        function
        | ParsedTime.Empty -> None
        | ParsedTime.Valid d -> Some <| formatter d
        | ParsedTime.Invalid str -> Some str